import React, { useState } from "react";
import {
  FloatingPortal,
  offset,
  safePolygon,
  size,
  useFloating,
  useHover,
  useInteractions,
  useTransitionStyles,
} from "@floating-ui/react";
import { twMerge } from "tailwind-merge";
import { CustomerAvatar } from "@components/CustomerAvatar/CustomerAvatar";
import { useCustomerDetailsQuery } from "@services/customer/queries";
import { CustomerDetailsMenu } from "./components/CustomerDetailsMenu";

interface CustomerDetailsProps {
  className?: string;
}

export const CustomerDetails: React.FC<CustomerDetailsProps> = (props) => {
  const { className } = props;

  const { data } = useCustomerDetailsQuery();

  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
    strategy: "fixed",
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(({ rects }) => ({
        mainAxis: 8,
        alignmentAxis: -rects.floating.width + 40,
      })),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            minWidth: `${12 * 16}px`,
          });
        },
      }),
    ],
  });

  const hover = useHover(context, {
    handleClose: safePolygon({
      requireIntent: false,
      buffer: 100,
    }),
  });

  const { styles: transitionStyles } = useTransitionStyles(context, {
    initial: {
      opacity: 0,
      transform: "scale(0.8)",
    },
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  if (!data) {
    return null;
  }

  return (
    <React.Fragment>
      <div
        ref={refs.setReference}
        className={twMerge("flex gap-3 text-sm", className)}
        {...getReferenceProps()}
      >
        <div>
          <CustomerAvatar />
        </div>
        <div className="flex flex-col">
          <h6>
            {data.firstName} {data.lastName}
          </h6>
          <p className="text-grey-dark">ID: {data.accountId}</p>
          {data.billForwardEmail && (
            <p className="text-grey-dark">{data.billForwardEmail}</p>
          )}
        </div>
      </div>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 40 }}
            {...getFloatingProps()}
          >
            <CustomerDetailsMenu style={transitionStyles} />
          </div>
        </FloatingPortal>
      )}
    </React.Fragment>
  );
};
